<template>
  <div v-bind="$attrs" class="fixed bottom-[25px] left-[calc(theme(spacing.4)+var(--sidebar-width))] z-[99] md:left-[calc(50px+var(--sidebar-width))]">
    <Popover
      ref="popover"
      placement="top-start"
      @hide="handleHide"
      @show="handleShow"
    >
      <template #trigger="{ active }">
        <KeywordListFloatingBtn
          :dragging-keyword="draggingKeyword"
          :list="defaultSelectedList"
          :loading="listsLoading.has(lastActiveListId)"
          :max="QUOTA.keyword_lists"
          :open="active"
          :show-selected-list="false"
          :variant
          @on-add-keyword-to-list="handleOnAddKeywordToList"
        />
      </template>
      <template #panel>
        <div class="size-fit">
          <div
            v-if="snapshot.matches('LISTING')"
            class="relative h-[400px] w-[320px]"
          >
            <Transition>
              <KeywordListSliderPanel
                v-if="selectedList"
                class="border-[--v3-input-field-inputborder)] absolute left-0 top-0 z-10 size-full translate-x-[calc(100%-6px)]
              overflow-hidden rounded-l-none rounded-r-[6px] border transition-transform duration-150"
                :list-name="selectedList?.name"
                :keywords="selectedList?.keywords"
                :color="selectedList?.color"
                :is-attempting-delete
                :is-deleting
                :deleting-keyword="deletingKeyword"
                @attempt-delete-list="handleAttemptDeleteList"
                @attempt-delete-keyword="handleAttemptDeleteKeyword"
                @cancel="handleCancelDelete"
                @confirm="handleConfirm"
              />
            </Transition>
            <KeywordListPanel
              class="absolute left-0 top-0 z-20 size-full overflow-hidden rounded-[6px] border border-[--v3-input-field-inputborder]"
              :lists
              :loading="isLoading"
              :selected-list-id="selectedList?.id"
              :dragging-keyword="draggingKeyword"
              :lists-loading="listsLoading"
              @toggle="handleToggle"
              @create="handleCreateNewList"
              @on-add-keyword-to-list="handleOnAddKeywordToList"
            />
          </div>
          <div v-else-if="snapshot.matches('CREATING')" class="w-[300px] overflow-hidden rounded-[6px] border border-[--v3-input-field-inputborder]">
            <KeywordListNewListPanel
              :creating="isPendingKeywordListCreate"
              @create="handleCreate"
              @return="handleList"
            />
          </div>
        </div>
      </template>
    </Popover>
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed, useTemplateRef, watch } from 'vue';

import Popover from '@/common/components/atoms/Popover';
import KeywordListFloatingBtn from '@/common/components/molecules/KeywordListFloatingBtn';
import KeywordListNewListPanel from '@/common/components/organisms/KeywordListNewListPanel';
import KeywordListPanel from '@/common/components/organisms/KeywordListPanel';
import KeywordListSliderPanel from '@/common/components/organisms/KeywordListSliderPanel';
import { mixPanelEvents } from '@/common/constants';
import {
  useKeywordListCreateMutation,
} from '@/common/queries/keyword-list';
import { useKeywordListStore } from '@/common/stores/lists';
import { useSubscriptionStore } from '@/common/stores/subscription';
import type { keywordListMachineActor, keywordListMachineSnapshop } from '@/common/types';
import { track } from '@/helpers/analytics/mixpanel';
import { getAppContext } from '@/helpers/get-app-context';

interface Props {
  actor: keywordListMachineActor,
  isDeleting: boolean,
  isDragging?: boolean,
  isLoading: boolean,
  lastActiveListId: number,
  lists: {
    color: string,
    id: number,
    keywords: {
      flags?: string,
      id: number,
      keyword: string,
      listId: number,
      notes?: string,
      tags?: number,
    }[]
    name: string,
  }[],
  listsLoading: Set<number>,
  snapshot: keywordListMachineSnapshop,
  variant?: 'green' | 'orange',
}

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<Props>(), {
  isDragging: false,
  variant: 'green',
});

const { QUOTA } = getAppContext();

const {
  mutate: mutateKeywordListCreate,
  isPending: isPendingKeywordListCreate,
} = useKeywordListCreateMutation();

const keywordListStore = useKeywordListStore();
const { lastActiveListId: _lastActiveListId } = storeToRefs(keywordListStore);
const {
  removeList,
  removeKeywordList,
  addTermsToList,
} = keywordListStore;
const { reloadQuotaNoneDaily } = useSubscriptionStore();

const selectedListId = computed<number>(() => props.snapshot.context.selectedList);
const selectedList = computed(() => props.lists.find((item) => item.id === selectedListId.value));
const isAttemptingDelete = computed(() => !!(props.snapshot.context.deletingListId || props.snapshot.context.deletingKeywordId));
const deletingKeyword = computed<string | null | undefined>(() => {
  const deletingKeywordId = props.snapshot.context.deletingKeywordId;

  if (!deletingKeywordId) return null;
  const selectedKeyword = selectedList.value.keywords.find((item) => item.id ===  deletingKeywordId);
  return selectedKeyword?.keyword;
});

const draggingKeyword = computed(() =>  props.snapshot.context.draggingKeyword);
const defaultSelectedList = computed(() => {
  if (!props.lastActiveListId) return null;

  return props.lists.find((item) => item.id === props.lastActiveListId);
});

const popoverRef = useTemplateRef('popover');

watch(() => props.isDragging, (newVal, oldVal) => {
  if (newVal && !oldVal) {
    popoverRef.value?.open();
  }
});

const handleToggle = (listId) => {
  props.actor.send({ type: 'select', listId });
  _lastActiveListId.value = listId;
};

const handleCreateNewList = () => {
  props.actor.send({ type: 'attempt_create' });
};

const handleCreate = (listName: string) => {
  mutateKeywordListCreate({
    name: listName,
  }, {
    onSuccess: () => {
      props.actor.send({ type: 'create' });
      reloadQuotaNoneDaily();
    },
  });
};

const handleList = () => {
  props.actor.send({ type: 'list' });
};

const handleCancelDelete = () => {
  props.actor.send({ type: 'cancel' });
};

const handleAttemptDeleteList = () => {
  props.actor.send({ type: 'attempt_delete_list', listId: selectedListId.value  });
};

const handleAttemptDeleteKeyword = (keywordId: number) => {
  props.actor.send({ type: 'attempt_delete_keyword', listId: selectedListId.value, keywordId  });
};

const handleConfirm = () => {
  if (props.snapshot.context.deletingKeywordId) {
    removeKeywordList(props.snapshot.context.deletingKeywordId).then(() => {
      props.actor.send({ type: 'confirm' });
    });
  } else {
    removeList(selectedListId.value).then(() => {
      reloadQuotaNoneDaily();
      props.actor.send({ type: 'confirm' });
    });
  }
};

const handleOnAddKeywordToList = async (listId, keyword) => {
  await addTermsToList(listId, keyword);
};



const handleHide = () => {
  props.actor.send({ type: 'reset' });
};

function handleShow() {
  track(mixPanelEvents.OPENED_KEYWORD_LIST_MODAL);
}
</script>

<style scoped>
.v-enter-from {
  @apply translate-x-0;
}

.v-leave-to {
  @apply translate-x-0;
}
</style>
